import {
  Button,
  Card,
  Grid,
  Stack,
  Table,
  TableBody,
  TableContainer,
  TablePagination,
} from '@mui/material';
import { TABLE_HEAD_REFERRAL_LIST } from 'src/careflow/common/table-head';
import { translateTableHead } from 'src/careflow/helpers/common';
import { LoadingTableRow, TableHeadCustom } from 'src/components/table';
import { ReferralTable } from '../../referral';
import NoResultsTableRow from 'src/careflow/common/NoResultsTableRow';
import useTable from 'src/hooks/useTable';
import { useEffect, useMemo } from 'react';
import useTenantConfigData from 'src/careflow/ph-core/multi-tenant/tenant/hook/useTenantConfigData';
import { referralDtoToReferralManager } from 'src/careflow/common/common-utils';
import { ReferralManager } from 'src/@types/crs/referral';
import { PaginatedReferralDto, ReferralService } from 'src/crs/referral/services/ReferralService';
import { PaginateQuery } from 'src/api/pagination/dtos';
import useNotiMessage from 'src/hooks/useNotiMessage';
import { i18nFunction } from 'src/careflow/ph-core/internationalization/hook/useLocales';
import { WrappedPatient } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import AddReferral from '../../inbound-referral/AddReferral';
import useObjectState from 'src/hooks/useObjectState';
import Iconify from 'src/components/Iconify';
import { checkAclValidation } from 'src/careflow/ph-core/admin/permissions/util/permission.utils';
import crsAcls from 'src/careflow/ph-core/admin/permissions/util/crs/crsAcls';
import { REFERRAL_CREATE_ERROR_MESSAGE } from '../../inbound-referral/constants';

export default function ReferralList({
  patient,
  i18n,
}: {
  patient: WrappedPatient | null;
  i18n: i18nFunction;
}) {
  const [{ tableData, loading, open, refresh }, updateState] = useObjectState<{
    tableData: PaginatedReferralDto | null;
    loading: boolean;
    open: boolean;
    refresh: boolean;
  }>({
    tableData: null,
    loading: false,
    open: false,
    refresh: false,
  });

  const { page, orderBy, rowsPerPage, onChangePage, onChangeRowsPerPage } = useTable({
    defaultRowsPerPage: 5,
    defaultOrderBy: 'createdOn',
    initialIndex: 1,
    defaultCurrentPage: 1,
  });

  const { showErrorMessage, showSuccessMessage } = useNotiMessage();
  const { configurations } = useTenantConfigData();

  const getPaginateQuery = (page: number, rowsPerPage: number) => {
    if (!configurations || !patient?.id) return null;
    const paginateQuery: PaginateQuery = {
      page: page,
      limit: rowsPerPage,
      sortBy: [[orderBy, 'DESC']],
    };

    paginateQuery.filter = {
      indexedFhirSubjectRefUri: `${configurations.fhir.baseUrl}/Patient/${patient?.id}`,
    };

    return paginateQuery;
  };

  const fetchReferralList = async (page: number, rowsPerPage: number) => {
    updateState({ loading: true });
    try {
      const query = getPaginateQuery(page, rowsPerPage);
      if (!query) return;
      const referrals = await ReferralService.getAll(query);
      updateState({ tableData: referrals });
      if (!referrals) return showErrorMessage(i18n('error.referral.list'));
    } catch (error) {
    } finally {
      updateState({ loading: false });
    }
  };

  useEffect(() => {
    fetchReferralList(page, rowsPerPage);
  }, [page, rowsPerPage, patient, configurations, refresh]);

  const rows = useMemo<ReferralManager[]>(
    () => (tableData?.data ? tableData.data.map((r) => referralDtoToReferralManager(r)) : []),
    [tableData]
  );

  const handlePageChange = (event: unknown, newPage: number) => {
    onChangePage(event, newPage + 1);
  };

  const handleServiceRequest = async (data: any) => {
    try {
      const payload = {
        serviceRequestId: data?.id,
        scope: data?.scope,
        patientId: patient?.id ?? '',
      };

      await ReferralService.create(payload);
      showSuccessMessage('Referral saved successfully');
    } catch (error) {
      showErrorMessage(`${REFERRAL_CREATE_ERROR_MESSAGE}`);
    } finally {
      updateState({ refresh: !refresh });
    }
  };

  return (
    <Card>
      <Stack direction="row" sx={{ py: 2, px: 2 }}>
        <Grid container alignItems="center" justifyContent="flex-end" xs={12}>
          {checkAclValidation({ acls: [crsAcls.CRS.PATIENT.REFERRALS.ADD] }) ? (
            <Button
              variant="contained"
              sx={{
                height: '100%',
                minWidth: '64px',
                maxWidth: '100%',
                overflow: 'hidden',
              }}
              startIcon={<Iconify icon={'eva:plus-fill'} />}
              onClick={() => updateState({ open: true })}
              data-name="referral-button-add"
            >
              {i18n('patients.details.referalAndRequest.buttonAdd', 'crs')}
            </Button>
          ) : null}
        </Grid>
      </Stack>
      <TableContainer>
        <Table>
          <TableHeadCustom headLabel={translateTableHead(TABLE_HEAD_REFERRAL_LIST, i18n, 'crs')} />
          <TableBody>
            {!loading ? (
              rows?.length ? (
                rows.map((row) => <ReferralTable key={row.id} row={row} />)
              ) : (
                <NoResultsTableRow
                  colSpan={TABLE_HEAD_REFERRAL_LIST.length}
                  text={i18n('referral.list.listNotFound', 'crs')}
                />
              )
            ) : (
              <LoadingTableRow colSpan={TABLE_HEAD_REFERRAL_LIST.length} />
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Grid xs={12}>
        <Stack justifyContent="flex-end">
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            count={tableData ? tableData.meta.totalItems : 0}
            rowsPerPage={rowsPerPage}
            page={page - 1}
            onPageChange={handlePageChange}
            onRowsPerPageChange={onChangeRowsPerPage}
            sx={{ borderTop: 0 }}
          />
        </Stack>
      </Grid>
      <AddReferral
        title={i18n('patients.details.serviceRequests.titleModal', 'crs')}
        openAdd={open}
        onReferralSuccess={(data) => handleServiceRequest(data)}
        patient={patient}
        onCancel={() => updateState({ open: false })}
        observations={null}
        conditions={null}
        showWorkflowScope={true}
      />
    </Card>
  );
}
